import { useMemo, useState } from "react";
import { APIProvider, Map, AdvancedMarker, InfoWindow, useMap } from "@vis.gl/react-google-maps";
import { useNavigate } from "react-router-dom";
import { MapPin, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { benchmarkFor, TYPE_BY_VALUE } from "../lib/propertyTypes";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_PUBLIC_KEY;
const MAP_ID = "realtyvault-portfolio-map";

const money = (n) => "$" + (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

// Returns { bg, ring, label, tone } for a given benchmark status.
function pinStyle(status) {
  switch (status) {
    case "above":   return { bg: "#059669", ring: "ring-emerald-200", label: "Above benchmark", Icon: TrendingUp };
    case "below":   return { bg: "#DC2626", ring: "ring-red-200",     label: "Below benchmark", Icon: TrendingDown };
    case "within":  return { bg: "#0EA5E9", ring: "ring-sky-200",     label: "Within benchmark", Icon: Minus };
    default:        return { bg: "#64748B", ring: "ring-slate-200",   label: "No benchmark", Icon: Minus };
  }
}

export default function PortfolioMap({ properties = [], height = 420 }) {
  // Filter down to geocoded properties only
  const geocoded = useMemo(
    () => (properties || []).filter((p) => typeof p.lat === "number" && typeof p.lng === "number"),
    [properties],
  );

  // Enrich with benchmark + color
  const markers = useMemo(() => geocoded.map((p) => {
    const cap = typeof p.cap_rate === "number" ? p.cap_rate : null;
    const bench = cap != null ? benchmarkFor(p.property_type, cap) : { status: "unknown" };
    const style = pinStyle(bench.status);
    return { ...p, cap, bench, style };
  }), [geocoded]);

  // Summary counts for the legend
  const counts = useMemo(() => {
    const c = { above: 0, within: 0, below: 0, unknown: 0 };
    for (const m of markers) c[m.bench.status] = (c[m.bench.status] || 0) + 1;
    return c;
  }, [markers]);

  if (!API_KEY) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-6 text-sm text-slate-500" data-testid="portfolio-map-missing-key">
        Google Maps key not configured. Set <code className="font-mono">REACT_APP_GOOGLE_MAPS_PUBLIC_KEY</code> in frontend/.env.
      </div>
    );
  }
  if (markers.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-10 text-center" data-testid="portfolio-map-empty">
        <MapPin className="w-8 h-8 mx-auto text-slate-300 mb-2" />
        <div className="overline text-slate-500">Portfolio map</div>
        <div className="text-sm text-slate-500 mt-2">Add properties with resolvable addresses — they'll appear here automatically once geocoded.</div>
      </div>
    );
  }

  const avgLat = markers.reduce((s, m) => s + m.lat, 0) / markers.length;
  const avgLng = markers.reduce((s, m) => s + m.lng, 0) / markers.length;

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="portfolio-map">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="overline text-slate-500">Geography</div>
          <h3 className="text-lg font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>
            Portfolio map
          </h3>
        </div>
        <Legend counts={counts} total={markers.length} />
      </div>
      <div style={{ height }}>
        <APIProvider apiKey={API_KEY}>
          <Map
            defaultCenter={{ lat: avgLat, lng: avgLng }}
            defaultZoom={4}
            mapId={MAP_ID}
            mapTypeId="satellite"
            gestureHandling="greedy"
            disableDefaultUI={false}
            mapTypeControl={true}
            streetViewControl={false}
            fullscreenControl={true}
            zoomControl={true}
          >
            <PortfolioPins markers={markers} />
          </Map>
        </APIProvider>
      </div>
    </div>
  );
}

function PortfolioPins({ markers }) {
  const map = useMap();
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState(null);

  // Auto-fit bounds to include every pin when the map first loads
  const fit = () => {
    if (!map || !window.google || markers.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    for (const m of markers) bounds.extend({ lat: m.lat, lng: m.lng });
    map.fitBounds(bounds, 80);
  };
  // Run once when map + markers available
  useMemo(() => {
    if (map && markers.length) {
      // micro-delay so Map is mounted
      setTimeout(fit, 50);
    }
  }, [map, markers.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const active = markers.find((m) => m.id === activeId);

  return (
    <>
      {markers.map((m) => (
        <AdvancedMarker
          key={m.id}
          position={{ lat: m.lat, lng: m.lng }}
          onClick={() => setActiveId(m.id)}
        >
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full ring-4 ring-white shadow-md transition-transform hover:scale-110 cursor-pointer"
            style={{ backgroundColor: m.style.bg }}
            data-testid={`map-pin-${m.id}`}
            title={`${m.name} — ${m.style.label}${m.cap != null ? ` (${m.cap.toFixed(2)}% cap)` : ""}`}
          >
            <span className="text-[13px]">{TYPE_BY_VALUE[m.property_type]?.emoji || "📍"}</span>
          </div>
        </AdvancedMarker>
      ))}

      {active && (
        <InfoWindow
          position={{ lat: active.lat, lng: active.lng }}
          onCloseClick={() => setActiveId(null)}
          pixelOffset={[0, -24]}
        >
          <div className="min-w-[240px] p-1" data-testid={`map-info-${active.id}`}>
            <div className="flex items-center gap-2">
              <span className="text-lg">{TYPE_BY_VALUE[active.property_type]?.emoji || "🏢"}</span>
              <div>
                <div className="font-bold text-slate-900 text-sm" style={{ fontFamily: "Outfit" }}>{active.name}</div>
                <div className="text-[11px] text-slate-500 font-mono">{active.formatted_address || active.address}</div>
              </div>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <Stat label="Market value" value={money(active.market_value)} />
              <Stat label="Units" value={active.units ?? "—"} />
              <Stat
                label="Cap rate"
                value={active.cap != null ? `${active.cap.toFixed(2)}%` : "—"}
                tone={
                  active.bench.status === "above" ? "good"
                  : active.bench.status === "below" ? "bad"
                  : "neutral"
                }
              />
              <Stat
                label="NOI"
                value={money(active.noi)}
              />
            </div>
            {active.bench?.min != null && (
              <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                Benchmark: {active.bench.min}–{active.bench.max}% typical · <span style={{ color: active.style.bg, fontWeight: 700 }}>{active.style.label}</span>
              </div>
            )}
            <button
              onClick={() => navigate("/dashboard/properties")}
              data-testid={`map-drilldown-${active.id}`}
              className="mt-3 w-full btn-primary-dark text-[11px] py-1.5 font-mono uppercase tracking-widest"
            >
              Open in Properties →
            </button>
          </div>
        </InfoWindow>
      )}
    </>
  );
}

function Stat({ label, value, tone }) {
  const cls = tone === "good" ? "text-emerald-700" : tone === "bad" ? "text-red-700" : "text-slate-900";
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest font-mono text-slate-500">{label}</div>
      <div className={`font-mono font-bold ${cls}`}>{value}</div>
    </div>
  );
}

function Legend({ counts, total }) {
  const items = [
    { key: "above",   color: "#059669", label: "Above" },
    { key: "within",  color: "#0EA5E9", label: "Within" },
    { key: "below",   color: "#DC2626", label: "Below" },
    { key: "unknown", color: "#64748B", label: "—" },
  ];
  return (
    <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest" data-testid="map-legend">
      <span className="text-slate-400">{total} mapped</span>
      {items.map((i) => (
        <span key={i.key} className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: i.color }} />
          <span className="text-slate-600">{i.label}</span>
          <span className="text-slate-400">({counts[i.key] || 0})</span>
        </span>
      ))}
    </div>
  );
}
